import { UIModel } from '../types/model.types';
import { UIModelAPI, GroupAPI, CanvasAPI, ContainerAPI } from '../types/api.types';
import { Group } from '../types/group.types';
import { Container } from '../types/canvas.types';

export const transformModelToAPI = ( model: UIModel, uiModelId: string, containers: { [pageId: string]: Container[] }, containerRows?: { [pageId: string]: Record<string, number> } ): UIModelAPI => {
  const canvasMap: { [key: string]: CanvasAPI } = {};
  
  const apiGroups: GroupAPI[] = model.layout.groups.map(group => {
    const apiPages = group.pages.map((tab, index) => {
      const canvasId = `canvas-${tab.id}`;
      
      if (containers[tab.id]) {
        const pageContainerRows = containerRows?.[tab.id];
        
        canvasMap[canvasId] = {
          pageId: tab.id,
          groupId: group.id,
          containers: transformContainersToAPI(containers[tab.id], pageContainerRows)
        };
      }
      
      return {
        id: tab.id,
        name: tab.name,
        canvasId: canvasId,
        order: tab.order,
        configuration: {
          autoAdvance: tab.configuration.autoAdvance,
          name: tab.name
        }
      };
    });
    
    return {
      id: group.id,
      name: group.name,
      order: group.order,
      orientation: group.orientation,
      configuration: {
        showTitle: group.configuration.showTitle,
        name: group.name
      },
      pages: apiPages
    };
  });
  
  const apiModel: UIModelAPI = {
    id: uiModelId,
    name: model.name,
    createdAt: model.createdAt,
    updatedAt: model.updatedAt,
    createdBy: model.createdBy,
    layout: {
      header: {
        configuration: model.layout.header
      },
      footer: {
        configuration: model.layout.footer
      },
      navigation: {
        configuration: (model as any).navigationConfig || {
          orientation: 'Horizontal',
          style: 'Block',
          textAlignment: 'Centre',
          showDivider: true,
          showNavigation: true,
          pageSpacing: 10,
          groupSpacing: 10
        },
        groups: apiGroups
      },
      canvas: canvasMap
    },
    theme: model.theme,
    customCss: model.customCSS,
    editorState: model.editorState
  };
  
  return apiModel;
};

export const transformAPIToModel = (apiModel: UIModelAPI): { model: UIModel, containers: { [pageId: string]: Container[] } } => {
  const groups: Group[] = apiModel.layout.navigation.groups.map(apiGroup => ({
    id: apiGroup.id,
    name: apiGroup.name,
    order: apiGroup.order,
    orientation: apiGroup.orientation as 'horizontal' | 'vertical',
    configuration: {
      showTitle: apiGroup.configuration.showTitle
    },
    pages: apiGroup.pages.map(page => ({
      id: page.id,
      name: page.name,
      order: page.order,
      configuration: {
        autoAdvance: page.configuration.autoAdvance
      },
      canvas: { containers: [] }
    }))
  }));
  
  const containers: { [pageId: string]: Container[] } = {};
  
  Object.values(apiModel.layout.canvas).forEach(canvas => {
    containers[canvas.pageId] = transformContainersFromAPI(canvas.containers);
  });
  
  const model: UIModel = {
    _id: apiModel.id,
    name: apiModel.name,
    createdAt: apiModel.createdAt,
    updatedAt: apiModel.updatedAt,
    createdBy: apiModel.createdBy,
    layout: {
      header: apiModel.layout.header.configuration,
      footer: apiModel.layout.footer?.configuration || {
        showFooter: true,
        showDescription: true,
        descriptionText: 'Footer description or disclaimer text',
        showPreviousButton: true,
        previousButtonText: 'Previous',
        previousButtonShowIcon: false,
        previousButtonShowTooltip: false,
        previousButtonTooltipText: '',
        showNextButton: true,
        nextButtonText: 'Next',
        nextButtonShowIcon: false,
        nextButtonShowTooltip: false,
        nextButtonTooltipText: '',
        previousButtonIconUrl: '',
        nextButtonIconUrl: ''
      },
      groups: groups
    },
    theme: apiModel.theme,
    customCSS: apiModel.customCss,
    editorState: apiModel.editorState
  };
  
  return { model, containers };
};

export const transformContainersToAPI = ( containers: Container[],  containerRows?: Record<string, number> ): ContainerAPI[] => {
  return containers.map(container => {
    const rowIndex = container.metadata?.isNested 
            ? (container.metadata?.rowIndex ?? 0)
            : (container.metadata?.rowIndex ?? containerRows?.[container.id]);
    return {
            id: container.id,
            name: container.name,
            order: container.order,
            configuration: container.configuration,
            content: {
                submodel: container.content.submodel,
                items: container.content.items || [],
                ...(container.content.nestedContainers && container.content.nestedContainers.length > 0 && {
                    nestedContainers: transformContainersToAPI(container.content.nestedContainers, containerRows)
                })
            },
            usageInfo: container.usageInfo,
            ...(rowIndex !== undefined && { rowIndex }),
            metadata: {
                type: container.metadata?.type,
                isNested: container.metadata?.isNested || false,
                ...(container.metadata?.parentContainerId && { 
                    parentContainerId: container.metadata.parentContainerId 
                }),
                ...(rowIndex !== undefined && { rowIndex })
            },
        };
    });
};

const transformContainersFromAPI = (apiContainers: ContainerAPI[], parentId?: string): Container[] => {
  return apiContainers.map((apiContainer, index) => {
    const nestedContainers = apiContainer.content.nestedContainers 
      ? transformContainersFromAPI(apiContainer.content.nestedContainers, apiContainer.id)
      : [];
    
    const isNested = apiContainer.metadata?.isNested ?? !!parentId;
    
    let calculatedRowIndex: number | undefined;
    
    if (apiContainer.metadata?.rowIndex !== undefined) {
        calculatedRowIndex = apiContainer.metadata.rowIndex;
    } else if (apiContainer.rowIndex !== undefined) {
        calculatedRowIndex = apiContainer.rowIndex;
    } else if (!isNested) {
        // Only calculate for parent containers if no rowIndex is provided
        calculatedRowIndex = calculateRowIndexFromLayout(apiContainers, index);
    }
    
    return {
      id: apiContainer.id,
      name: apiContainer.name,
      order: apiContainer.order,
      configuration: apiContainer.configuration,
      metadata: {
        type: (isNested ? 'nested-container' : 'container') as 'container' | 'nested-container',
        isNested: isNested,
        parentContainerId: apiContainer.metadata?.parentContainerId || parentId,
        ...(calculatedRowIndex !== undefined && { rowIndex: calculatedRowIndex })
      },
      content: {
        type: apiContainer.content.submodel ? 'subModel' : 'widget',
        componentId: apiContainer.content.submodel?.id || 'empty',
        'widget-type': 'contentBlock',
        nestedContainers: nestedContainers,
        submodel: apiContainer.content.submodel,
        items: apiContainer.content.items || []
      },
      usageInfo: apiContainer.usageInfo
    };
  });
};

const calculateRowIndexFromLayout = (apiContainers: ContainerAPI[], currentIndex: number): number => {
  let currentRow = 0;
  let currentRowWidth = 0;
  
  for (let i = 0; i <= currentIndex; i++) {
    const container = apiContainers[i];
    const width = parseFloat(container.configuration.width) || 100;
    
    if (currentRowWidth + width > 100.01 || width >= 100) {
      if (i === currentIndex) {
        return currentRow;
      }
      currentRow++;
      currentRowWidth = width;
    } else {
      currentRowWidth += width;
    }
  }
  
  return currentRow;
};

export const extractContainerRows = (containers: Container[]): Record<string, number> => {
  const containerRows: Record<string, number> = {};
  
  const processContainers = (containerList: Container[]) => {
    containerList.forEach((container, index) => {
      if (!container.metadata?.isNested) {
        if (container.metadata?.rowIndex !== undefined) {
          containerRows[container.id] = container.metadata.rowIndex;
        } else {
          const calculatedRow = calculateRowFromContainerLayout(containerList, index);
          containerRows[container.id] = calculatedRow;
        }
      }
      
      if (container.content.nestedContainers) {
        processContainers(container.content.nestedContainers);
      }
    });
  };
  
  processContainers(containers);
  return containerRows;
};

const calculateRowFromContainerLayout = (containers: Container[], currentIndex: number): number => {
  let currentRow = 0;
  let currentRowWidth = 0;
  
  for (let i = 0; i <= currentIndex; i++) {
    const container = containers[i];
    const width = parseFloat(container.configuration.width) || 100;
    
    if (currentRowWidth + width > 100.01 || width >= 100) {
      if (i === currentIndex) {
        return currentRow;
      }
      currentRow++;
      currentRowWidth = width;
    } else {
      currentRowWidth += width;
    }
  }
  
  return currentRow;
};
